import { ButtonWithIcon } from '../../button-with-icon';
import styles from './styles.module.scss';
import cn from 'classnames';
import { ReactNode } from 'react';

export interface FlatSelectOption<T = string> {
  id: T;
  label: string;
  disabled?: boolean;
  deletable?: boolean;
  iconPath?: string;
  customElement?: ReactNode;
}
export interface FlatSelectProps<T = string> {
  options: FlatSelectOption<T>[];
  value?: T | T[];
  onChange: (value: T | T[]) => void;
  onDelete?: (id: T) => void;
  isLoading?: boolean;
  mutedId?: T;
  deleteIconPath?: string;
  className?: string;
  direction?: 'vertical' | 'horizontal';
  btnSize?: 'sm' | 'lg';
  label?: string;
  multiple?: boolean;
  showCheckbox?: boolean;
  checkboxPosition?: 'left' | 'right';
  iconSize?: number;
  iconPosition?: 'left' | 'right';
  customElementPosition?: 'left' | 'right';
}

export const SelectInputFlat = <T,>({
  options,
  value,
  onChange,
  onDelete,
  isLoading = false,
  mutedId,
  deleteIconPath = '/media/vehicle-preparation/xIcon.svg',
  className = '',
  direction = 'vertical',
  btnSize = 'lg',
  label,
  multiple = false,
  showCheckbox = false,
  checkboxPosition = 'left',
  iconSize = 24,
  iconPosition = 'right',
  customElementPosition = 'right'
}: FlatSelectProps<T>) => {
  const isDeleting = (id: T) => isLoading && id === mutedId;

  const isSelected = (id: T): boolean => {
    if (multiple && Array.isArray(value)) {
      return value.includes(id);
    }
    return value === id;
  };

  const handleOptionClick = (optionId: T, optionDisabled?: boolean) => {
    const deleting = isDeleting(optionId);
    if (optionDisabled || deleting) return;

    if (multiple) {
      const currentValue = Array.isArray(value) ? value : [];
      const newValue = currentValue.includes(optionId)
        ? currentValue.filter((id) => id !== optionId)
        : [...currentValue, optionId];
      onChange(newValue);
    } else {
      onChange(optionId);
    }
  };

  const renderCheckbox = (optionId: T, selected: boolean, optionDisabled?: boolean) => {
    if (!showCheckbox) return null;

    return (
      <div
        className={cn('form-check form-check-custom form-check-solid', checkboxPosition === 'left' ? 'me-3' : 'ms-3')}
      >
        <input
          className="form-check-input"
          type="checkbox"
          checked={selected}
          onChange={() => handleOptionClick(optionId, optionDisabled)}
          disabled={optionDisabled || isDeleting(optionId)}
        />
      </div>
    );
  };

  const renderIcon = (iconPath?: string) => {
    if (!iconPath) return null;

    return (
      <div
        className={cn(
          'd-flex align-items-center justify-content-center rounded-circle overflow-hidden',
          iconPosition === 'left' ? 'me-3' : 'ms-3',
          styles.iconContainer
        )}
        style={{ width: iconSize, height: iconSize, flexShrink: 0 }}
      >
        <img
          src={iconPath}
          alt=""
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          onError={(e) => {
            e.currentTarget.style.display = 'none';
          }}
        />
      </div>
    );
  };

  const renderCustomElement = (customElement?: ReactNode) => {
    if (!customElement) return null;
    return <div className={cn(customElementPosition === 'left' ? 'me-3' : 'ms-3')}>{customElement}</div>;
  };

  return (
    <div className={cn('d-flex flex-column gap-2', className)}>
      {label && <label className="form-label fw-semibold text-gray-700">{label}</label>}
      <div
        className={cn(
          'd-flex',
          direction === 'vertical'
            ? 'flex-column justify-content-start align-items-stretch gap-3'
            : 'flex-row flex-wrap gap-2'
        )}
      >
        {options.map((option) => {
          const deleting = isDeleting(option.id);
          const selected = isSelected(option.id);

          return (
            <div key={String(option.id)} className="d-flex flex-row justify-content-between gap-2 align-items-center">
              <div className="d-flex flex-row align-items-center flex-grow-1">
                <div
                  onClick={() => handleOptionClick(option.id, option.disabled)}
                  className={cn(
                    'd-flex flex-row btn flex-grow-1 align-items-center',
                    deleting && 'disabled btn-light-danger',
                    !deleting && selected && 'btn-secondary',
                    !deleting && !selected && !option.disabled && 'btn-light-secondary',
                    option.disabled && 'disabled btn-light',
                    !option.disabled && !deleting && 'cursor-pointer',
                    {
                      'btn-sm': btnSize === 'sm',
                      'btn-lg': btnSize === 'lg'
                    }
                  )}
                >
                  {checkboxPosition === 'left' && renderCheckbox(option.id, selected, option.disabled)}
                  {customElementPosition === 'left' && renderCustomElement(option.customElement)}
                  {iconPosition === 'left' && renderIcon(option.iconPath)}
                  <span className="flex-grow-1 text-start">{deleting ? 'Deleting...' : option.label}</span>
                  {iconPosition === 'right' && renderIcon(option.iconPath)}
                  {customElementPosition === 'right' && renderCustomElement(option.customElement)}
                  {checkboxPosition === 'right' && renderCheckbox(option.id, selected, option.disabled)}
                </div>
              </div>
              {onDelete && option.deletable && option.id !== mutedId && (
                <div className={styles.fixedWidthBtn}>
                  <ButtonWithIcon
                    disabled={option.disabled || isLoading}
                    isLoading={deleting}
                    onClick={() => onDelete(option.id)}
                    btnStyle="light-secondary"
                    btnSize="sm"
                    iconPath={deleteIconPath}
                    className={styles.fixedButton}
                  >
                    {null}
                  </ButtonWithIcon>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
